import { useTranslation } from 'react-i18next';

import type { RisingStarAssetSourceType, RisingStarBonusStatus } from 'services/hooks/risingStarBonus';

import { formatAmount } from './format';
import { formatPayoutDate } from '../minimumGuarantee/utils/formatSettlement';

const K = 'chat.rewards_rising_star_';

/**
 * 'YYYY-MM' 을 화면 언어로 — ko '10월', en 'October', ja '10月'.
 * 태국어 기본 달력은 불기(2569)라 다른 날짜(KST 서기 표기)와 어긋나므로 서기로 고정한다.
 */
const formatTargetMonth = (targetMonth: string, language: string, withYear: boolean) => {
  const year = Number(targetMonth.slice(0, 4));
  const month = Number(targetMonth.slice(5, 7));
  if (!year || !month) return targetMonth;

  return new Intl.DateTimeFormat(`${language}-u-ca-gregory`, {
    timeZone: 'UTC',
    month: 'long',
    ...(withYear && { year: 'numeric' }),
  }).format(new Date(Date.UTC(year, month - 1, 1)));
};

/** 샛별 달성 보너스 섹션 문구 (studio#2524) */
export const useRisingStarCopy = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'ko';
  const month = (targetMonth: string) => formatTargetMonth(targetMonth, language, false);
  const tierRange = (threshold: number) => t(`${K}threshold_or_more`, { string_amount: formatAmount(threshold) });
  /** 구간 보너스 — 고정이면 '+1,500', 30,000 구간(bonus null)은 비율 */
  const tierBonus = (bonus: number | null) =>
    bonus === null ? t(`${K}achievement_fifty_percent`) : `+${formatAmount(bonus)}`;

  return {
    title: t(`${K}title`),
    // '이번 달' 대신 산정 월을 쓴다 — 25일 뒤에 샛별이 된 유저는 이번 달이 아니라 다음 달 ESTIMATED 를 받는다 (BE 가이드)
    status: {
      ESTIMATED: {
        label: (m: string) => t(`${K}estimated_monthly_bonus`, { string_month: month(m) }),
        chip: t(`${K}estimated`),
      },
      CALCULATED: {
        label: (m: string) => t(`${K}scheduled_monthly_bonus`, { string_month: month(m) }),
        chip: t(`${K}calculated`),
      },
      PAID: {
        label: (m: string) => t(`${K}paid_monthly_bonus`, { string_month: month(m) }),
        chip: t(`${K}paid`),
      },
      CANCELLED: {
        label: (m: string) => t(`${K}monthly_achievement_bonus`, { string_month: month(m) }),
        chip: t(`${K}unpaid`),
      },
    } satisfies Record<RisingStarBonusStatus, { label: (targetMonth: string) => string; chip: string }>,
    statusSub: {
      ESTIMATED: (asOfAt: string, payoutDate: string) =>
        t(`${K}estimate_description`, { string_asOfAt: asOfAt, string_payoutDate: payoutDate }),
      CALCULATED: (asOfAt: string, payoutDate: string) =>
        t(`${K}calculated_description`, { string_asOfAt: asOfAt, string_payoutDate: payoutDate }),
      PAID: (_asOfAt: string, payoutDate: string) => t(`${K}paid_description`, { string_payoutDate: payoutDate }),
      CANCELLED: (_asOfAt: string, payoutDate: string) =>
        t(`${K}not_morning_star_on_payout_date`, { string_payoutDate: payoutDate }),
    } satisfies Record<RisingStarBonusStatus, (asOfAt: string, payoutDate: string) => string>,
    nextCalculateAt: t(`${K}next_calculation_date`),
    payoutDate: t(`${K}payout_date`),
    period: t('chat.rewards_mg_settlement_period'),
    tierTitle: t(`${K}achievement_tiers`),
    nextTier: (thresholdFormat: string, remainingFormat: string, bonusLabel: string) =>
      t(`${K}next_tier_description`, {
        string_threshold: thresholdFormat,
        string_remaining: remainingFormat,
        string_bonus: bonusLabel,
      }),
    topTier: t(`${K}highest_tier_description`),
    tierRange,
    tierBonus,
    calcTitle: t(`${K}calculation_details`),
    totalReward: t(`${K}combined_rewards`),
    appliedTier: t(`${K}applied_tier`),
    appliedRule: t(`${K}applied_rule`),
    bonus: t(`${K}achievement_bonus`),
    ruleLabel: {
      FIXED: t(`${K}fixed_bonus`),
      HALF_ROUND_UP: t(`${K}achievement_fifty_percent_rounded_up`),
      NONE: t('chat.tap_asset_more_options_modal_none'),
    },
    notAchieved: t(`${K}not_achieved`),
    assetTitle: t(`${K}eligible_assets`),
    assetSub: t(`${K}within_ninety_days_of_first_release`),
    assetType: {
      persona: t('chat.custom_character'),
      universe: t('chat.custom_universe'),
      scene: t('chat.custom_scene'),
    } satisfies Record<RisingStarAssetSourceType, string>,
    firstPublished: t(`${K}first_release`),
    excluded: t(`${K}excluded`),
    total: t(`${K}total`),
    expand: (count: number) => t(`${K}expand_all`, { int_count: count }),
    collapse: t('chat.information_comment_show_less'),
    emptyTitle: t(`${K}no_new_assets`),
    emptyBody: t(`${K}new_asset_description`),
    historyTitle: t(`${K}bonus_history`),
    stargazerHistoryTitle: t(`${K}morning_star_bonus_history`),
    recovered: t(`${K}bonus_reclaimed`),
    historyMonth: (targetMonth: string) =>
      t(`${K}for_year_month`, { string_yearMonth: formatTargetMonth(targetMonth, language, true) }),
    historyPaidAt: (payoutDate: string) =>
      t(`${K}payout_date_label`, { string_payoutDate: formatPayoutDate(payoutDate) ?? '-' }),
    baseReward: t(`${K}base_rewards`),
    monthTotal: t(`${K}monthly_total_rewards`),
    noticeTitle: t(`${K}bonus_guide`),
    // 정책 B-1 안내 문구 (v1.2, 6줄)
    notice: [
      t(`${K}calculation_notice`),
      t(`${K}non_cumulative_notice`),
      t(`${K}rounding_notice`),
      t(`${K}first_release_notice`),
      t(`${K}recalculation_notice`),
      t(`${K}rate_change_notice`),
    ],
  };
};
